import {
  AdminCatalogResponse,
  AdminCatalogType,
  type AdminCatalogTypeUpdateRequest,
  AdminCatalogVariant,
  type AdminCatalogVariantUpdateRequest,
  AdminErrorEnvelope,
  AdminFeatureFlag,
  AdminFeatureFlagsResponse,
  AdminLegalDocument,
  AdminLegalDocumentsResponse,
  AdminLoginResponse,
  AdminMeResponse,
  AdminOrderDetail,
  AdminOrderListResponse,
  AdminSettingsResponse,
  AdminSettingUpdateResponse,
  AdminStatsResponse,
  type OrderStatus,
  type ProductionNoteSource,
} from "@pufimi/shared";

// The console talks to the same API as the public site (VITE_API_URL). Requests
// carry the admin-session cookie via `credentials: "include"`, and responses are
// validated against the shared admin contracts.
export const apiBaseUrl = import.meta.env.VITE_API_URL ?? "";

export class AdminApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message?: string) {
    super(message ?? code);
    this.name = "AdminApiError";
    this.status = status;
    this.code = code;
  }
}

async function request(path: string, init?: RequestInit): Promise<unknown> {
  const res = await fetch(`${apiBaseUrl}${path}`, { credentials: "include", ...init });
  if (!res.ok) {
    let code = "internal_error";
    let message: string | undefined;
    try {
      const parsed = AdminErrorEnvelope.safeParse(await res.json());
      if (parsed.success) {
        code = parsed.data.error.code;
        message = parsed.data.error.message;
      }
    } catch {
      /* non-JSON error body — keep the fallback code */
    }
    throw new AdminApiError(res.status, code, message);
  }
  return res.json();
}

export const adminApi = {
  async me(): Promise<AdminMeResponse> {
    return AdminMeResponse.parse(await request("/admin/me"));
  },
  async login(password: string): Promise<AdminLoginResponse> {
    return AdminLoginResponse.parse(
      await request("/admin/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      }),
    );
  },

  // Read-only dashboard overview (#245): headline totals, per-status counts, and
  // a trailing daily series for the Pulpit.
  async stats(): Promise<AdminStatsResponse> {
    return AdminStatsResponse.parse(await request("/admin/stats"));
  },

  // Read-only order views (#239). Both behind `requireAdmin`, so the session
  // cookie rides along via `credentials: "include"` in `request`.
  orders: {
    // One page of the order list, optionally filtered by status (#239, paged in
    // #245). Both are query params the API validates (`status` against the
    // `order_status` enum, `page` coerced to a 1-based int). The response carries
    // `total` / `page` / `pageSize` for the prev-next controls.
    async list(
      params: { status?: OrderStatus; page?: number } = {},
    ): Promise<AdminOrderListResponse> {
      const qs = new URLSearchParams();
      if (params.status) qs.set("status", params.status);
      if (params.page && params.page > 1) qs.set("page", String(params.page));
      const suffix = qs.toString() ? `?${qs.toString()}` : "";
      return AdminOrderListResponse.parse(await request(`/admin/orders${suffix}`));
    },
    // The assembled detail of one order + its event timeline.
    async get(orderId: string): Promise<AdminOrderDetail> {
      return AdminOrderDetail.parse(await request(`/admin/orders/${orderId}`));
    },
    // Tick/un-tick a fulfillment checklist step (#243). The API appends the
    // order_event (stamping the admin actor), applies any coarse status change
    // in one transaction, and returns the re-projected detail — so the caller
    // drops the result straight into the cache (status + checklist + timeline).
    async applyStep(
      orderId: string,
      body: {
        step: string;
        action: "tick" | "untick";
        // The line a per-line PRODUCTION step targets (ADR-0054 §5, #408). Required
        // for a line-scoped step, omitted for an order-level one.
        orderLineId?: string;
        // Shipment tracking captured with the `handed_to_carrier` tick (#315).
        trackingNumber?: string;
        trackingCarrier?: string;
      },
    ): Promise<AdminOrderDetail> {
      return AdminOrderDetail.parse(
        await request(`/admin/orders/${orderId}/steps`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }),
      );
    },
    // Append an internal note (#244). The API stamps the admin actor, appends the
    // append-only `internal_note` event, and returns the re-projected detail — so
    // the caller drops the result straight into the cache (the notes thread
    // updates without a refetch). `text` is trimmed + length-bounded server-side.
    async addNote(orderId: string, body: { text: string }): Promise<AdminOrderDetail> {
      return AdminOrderDetail.parse(
        await request(`/admin/orders/${orderId}/notes`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }),
      );
    },

    // Factory-files packet (#282/#283). Run the producer-packet generator
    // synchronously: the API records a `production_files_generated` order event
    // (lighting up the checklist step) and returns the re-projected detail — so
    // the caller drops it straight into the cache (the file grid + checklist
    // update in one shot). Idempotent server-side (re-running upserts + re-lights).
    async generateProductionFiles(orderId: string): Promise<AdminOrderDetail> {
      return AdminOrderDetail.parse(
        await request(`/admin/orders/${orderId}/production-files:generate`, { method: "POST" }),
      );
    },
    // Append one producer-channel note tagged `operator | factory` (#282). The API
    // stamps the admin actor as author and returns the re-projected detail — so
    // the caller drops it into the cache (the producer-notes thread grows without
    // a refetch). `body` is trimmed + length-bounded server-side.
    async addProductionNote(
      orderId: string,
      body: { source: ProductionNoteSource; body: string; orderLineId?: string | null },
    ): Promise<AdminOrderDetail> {
      return AdminOrderDetail.parse(
        await request(`/admin/orders/${orderId}/production-notes`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }),
      );
    },
    // The on-demand "send to factory" bundle (#282). A binary `application/zip`
    // stream, so it bypasses the JSON `request` helper — but still rides the admin
    // session cookie via `credentials: "include"`. Fetched as a Blob so the caller
    // can trigger a browser download (an object URL) that reliably carries the
    // cookie cross-origin. Throws an `AdminApiError` on a non-2xx so the UI can
    // surface "nothing generated yet" (409) distinctly.
    async exportProductionFilesZip(orderId: string): Promise<Blob> {
      const res = await fetch(`${apiBaseUrl}/admin/orders/${orderId}/production-files/export.zip`, {
        credentials: "include",
      });
      if (!res.ok) {
        let code = "export_failed";
        try {
          const parsed = AdminErrorEnvelope.safeParse(await res.json());
          if (parsed.success) code = parsed.data.error.code;
        } catch {
          /* non-JSON error body — keep the fallback code */
        }
        throw new AdminApiError(res.status, code);
      }
      return res.blob();
    },
  },

  // Universal Settings tab (#240). Both behind `requireAdmin`; the session cookie
  // rides along via `credentials: "include"`.
  settings: {
    // Every registry setting with its metadata + current effective value.
    async list(): Promise<AdminSettingsResponse> {
      return AdminSettingsResponse.parse(await request("/admin/settings"));
    },
    // Persist one setting's value; the API validates it against the registry
    // schema and returns the updated field.
    async update(key: string, value: unknown): Promise<AdminSettingUpdateResponse> {
      return AdminSettingUpdateResponse.parse(
        await request(`/admin/settings/${encodeURIComponent(key)}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ value }),
        }),
      );
    },
  },

  // Feature-flags tab (#241) — kill-switches over the existing flag table
  // (ADR-0031). Same `requireAdmin` + cookie story as settings above.
  featureFlags: {
    // Every flag with its state + last-changed audit columns.
    async list(): Promise<AdminFeatureFlagsResponse> {
      return AdminFeatureFlagsResponse.parse(await request("/admin/feature-flags"));
    },
    // Flip one flag's enabled state; the API stamps the actor + returns the flag.
    async setEnabled(name: string, enabled: boolean): Promise<AdminFeatureFlag> {
      return AdminFeatureFlag.parse(
        await request(`/admin/feature-flags/${encodeURIComponent(name)}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ enabled }),
        }),
      );
    },
  },

  // Katalog tab (#404, extending the edit-only #242 surface) — reads/writes the
  // existing product types + their generic variants. Same `requireAdmin` + cookie
  // story. NO create/delete: a new type is a migration + bespoke build (ADR-0054 §6).
  catalog: {
    // Every product type (id-ordered) with its editable metadata + variants.
    async list(): Promise<AdminCatalogResponse> {
      return AdminCatalogResponse.parse(await request("/admin/catalog"));
    },
    // Persist one type's metadata + active toggle; the API validates + returns the
    // updated type (with its variants). The active toggle gates buyer visibility.
    async updateType(id: string, body: AdminCatalogTypeUpdateRequest): Promise<AdminCatalogType> {
      return AdminCatalogType.parse(
        await request(`/admin/catalog/product-types/${encodeURIComponent(id)}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }),
      );
    },
    // Persist one variant's price/currency/attributes; the API validates (price
    // non-negative, attributes an object with no sibling collision) and returns the
    // updated row. Never touches placed orders (prices are snapshotted at checkout).
    async updateVariant(
      id: string,
      body: AdminCatalogVariantUpdateRequest,
    ): Promise<AdminCatalogVariant> {
      return AdminCatalogVariant.parse(
        await request(`/admin/catalog/variants/${encodeURIComponent(id)}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }),
      );
    },
  },

  // Legal-documents CMS (#305) — append-only versioned markdown for the Regulamin +
  // Polityka prywatności. Same `requireAdmin` + cookie story. The write model is
  // append-only: `publish` mints a new immutable version, `saveDraft` upserts the
  // single working copy.
  legalDocuments: {
    // Every known document with its published/draft state (the section index).
    async list(): Promise<AdminLegalDocumentsResponse> {
      return AdminLegalDocumentsResponse.parse(await request("/admin/legal-documents"));
    },
    // The editable buffer + published state for one document.
    async get(slug: string): Promise<AdminLegalDocument> {
      return AdminLegalDocument.parse(
        await request(`/admin/legal-documents/${encodeURIComponent(slug)}`),
      );
    },
    // Save (upsert) the single working draft — never publishes. Returns the
    // refreshed editor state.
    async saveDraft(slug: string, contentMd: string): Promise<AdminLegalDocument> {
      return AdminLegalDocument.parse(
        await request(`/admin/legal-documents/${encodeURIComponent(slug)}/draft`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ contentMd }),
        }),
      );
    },
    // Mint a new immutable published version from the given markdown. Returns the
    // refreshed editor state (new latest version).
    async publish(slug: string, contentMd: string): Promise<AdminLegalDocument> {
      return AdminLegalDocument.parse(
        await request(`/admin/legal-documents/${encodeURIComponent(slug)}/publish`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ contentMd }),
        }),
      );
    },
  },
};
